import { createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import set from 'lodash/set';
import { pender } from 'redux-pender';
import {
	postEncarpayApplications,
	getEncarpayApplicationsById,
	postEncarpayClients,
	getEncarpayVehiclesHyundaiLimit,
	postEncarpayAgreements,
	putEncarpayClientAddress,
	getEncarpayVehiclesJBLimit,
	getEncarpayVehiclesBNKLimit,
	postEncarpayVehiclesHyundaiLimit,
	postEncarPayReapplyCreditLimit,
} from 'app/api';

import { getOneStopEncarPayCreditLimit } from '@encarpkg/api';
import { loggingException } from 'app/libs/sentry';
import { ENCARPAY_REAPPLY_ERROR_CODE, ENCARPAY_REAPPLY_ERROR_INFO, JUDGEMENT_KEY } from 'app/constants/encarpay';

const INIT = 'encarpay.apply/INIT';
export const initAction = createAction(INIT);

const CHANGE = 'encarpay.apply/CHANGE';
export const changeAction = createAction(CHANGE);

const POST_ENCARPAY_APPLICATIONS = 'encarpay.apply/POST_ENCARPAY_APPLICATIONS';
export const postEncarpayApplicationsAction = createAction(POST_ENCARPAY_APPLICATIONS, postEncarpayApplications);

const GET_ENCARPAY_APPLICATIONS_BY_ID = 'encarpay.apply/GET_ENCARPAY_APPLICATIONS_BY_ID';
export const getEncarpayApplicationsByIdAction = createAction(GET_ENCARPAY_APPLICATIONS_BY_ID, getEncarpayApplicationsById);

const POST_ENCARPAY_CLIENTS = 'encarpay.apply/POST_ENCARPAY_CLIENTS';
export const postEncarpayClientsAction = createAction(POST_ENCARPAY_CLIENTS, postEncarpayClients);

const POST_ENCARPAY_AGREEMENTS = 'encarpay.apply/POST_ENCARPAY_AGREEMENTS';
export const postEncarpayAgreementsAction = createAction(POST_ENCARPAY_AGREEMENTS, postEncarpayAgreements);

const GET_ENCARPAY_ONESTOP_CREDIT_LIMIT = 'encarpay.apply/GET_ENCARPAY_ONESTOP_HYUNDAI_CREDIT_LIMIT';
export const getEncarpayOneStopLimitAction = createAction(GET_ENCARPAY_ONESTOP_CREDIT_LIMIT, getOneStopEncarPayCreditLimit);

const GET_ENCARPAY_HYUNDAI_CREDIT_LIMIT = 'encarpay.apply/GET_ENCARPAY_HYUNDAI_CREDIT_LIMIT';
export const getEncarpayVehiclesHyundaiLimitAction = createAction(GET_ENCARPAY_HYUNDAI_CREDIT_LIMIT, getEncarpayVehiclesHyundaiLimit);
const POST_ENCARPAY_HYUNDAI_CREDIT_LIMIT = 'encarpay.apply/POST_ENCARPAY_HYUNDAI_CREDIT_LIMIT';
export const postEncarpayVehiclesHyundaiLimitAction = createAction(POST_ENCARPAY_HYUNDAI_CREDIT_LIMIT, postEncarpayVehiclesHyundaiLimit);

const GET_ENCARPAY_JB_CREDIT_LIMIT = 'encarpay.apply/GET_ENCARPAY_JB_CREDIT_LIMIT';
export const getEncarPayJBCreditLimitAction = createAction(GET_ENCARPAY_JB_CREDIT_LIMIT, getEncarpayVehiclesJBLimit);

const GET_ENCARPAY_BNK_CREDIT_LIMIT = 'encarpay.apply/GET_ENCARPAY_BNK_CREDIT_LIMIT';
export const getEncarPayBNKCreditLimitAction = createAction(GET_ENCARPAY_BNK_CREDIT_LIMIT, getEncarpayVehiclesBNKLimit);

const PUT_ENCARPAY_CLIENT_ADDRESS = 'encarpay.apply/PUT_ENCARPAY_CLIENT_ADDRESS';
export const putEncarpayClientAddressAction = createAction(PUT_ENCARPAY_CLIENT_ADDRESS, putEncarpayClientAddress);

const POST_ENCARPAY_REAPPLY_CREDIT_LIMIT = 'reapply/GET_CREDIT_LIMIT';
export const postEncarpayReapplyCreditLimitAction = createAction(POST_ENCARPAY_REAPPLY_CREDIT_LIMIT, postEncarPayReapplyCreditLimit);

const initEncarPayState = {
	carId: null,
	applicationId: null,
	carMaximumLimit: null,
	showTerms: false,
	showEncarpayLimitLayer: false,
	inflow: null,
	applicationStorage: {},
	interestInfo: [],
	error: null,
	judgementStatus: null,
	loanTerms: {
		loanPersonalInfo: false,
		loanPersonalOffer: false,
	},
	redirectURL: '',
	backURL: '',
	mainInterestRate: 0,
	oneStopInfo: {},
	abTestType: null,
};

const initialState = initEncarPayState;

const onError = (state, action) =>
	produce(state, (draft) => {
		const error = action.payload;

		if (!error.response) {
			if (error.code === 'ECONNABORTED') {
				draft.error = {
					errorCode: 'ECONNABORTED',
				};
			}

			return;
		}

		loggingException(error);

		draft.error = error.response.data;
	});

const onErrorReapply = (state, action) =>
	produce(state, (draft) => {
		const error = action.payload;
		if (error?.message === 'CANCEL_ENCARPAY_LIMIT') return;
		const { data, status } = error.response;

		const initReapplyData = () => {
			draft.applicationId = null;
			draft.carMaximumLimit = null;
			draft.judgementStatus = null;
			draft.interestInfo = [];
		};

		draft.error = { errorCode: ENCARPAY_REAPPLY_ERROR_CODE.ERROR_UNDEFINED };

		if (status === 400 || status === 404) {
			const errorObj = ENCARPAY_REAPPLY_ERROR_INFO.find((i) => i.errorCode === data?.errorCode);
			if (errorObj) {
				draft.error = errorObj;
			}
		}

		initReapplyData();
		loggingException(error);
	});

export default handleActions(
	{
		[INIT]: (state) => produce(state, () => ({ ...initEncarPayState })),
		[CHANGE]: (state, action) =>
			produce(state, (draft) => {
				const { path, value } = action.payload;

				set(draft, path, value);
			}),
		...pender({
			type: GET_ENCARPAY_APPLICATIONS_BY_ID,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const {
						data: { id, inflow, financialCompany, financialCompanyName, interestRate, judgement, baseInterestRates },
						data,
					} = action.payload;
					const judgementKey = JUDGEMENT_KEY[financialCompany];

					draft.inflow = inflow;
					draft.applicationId = id;
					draft.carMaximumLimit = data[judgementKey].carMaximumLimit;
					draft.judgementStatus = judgement.status;
					draft.applicationStorage = {
						...draft.applicationStorage,
						[id]: data,
					};
					draft.interestInfo = [{ financialCompany, financialCompanyName, interestRate }, ...(baseInterestRates ?? [])];
					draft.mainInterestRate = interestRate;
				}),
		}),
		// OneStop - 현재는 현대캐피탈만 시행
		...pender({
			type: GET_ENCARPAY_ONESTOP_CREDIT_LIMIT,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const {
						data: { carMaximumLimit, interestRate, judgementStatus, maximumMonths },
					} = action.payload;

					draft.oneStopInfo = {
						carMaximumLimit,
						interestRate,
						judgementStatus,
						maximumMonths,
						interestInfo: [{ financialCompany: 'HYUNDAICAPITAL', financialCompanyName: '현대캐피탈', interestRate }],
					};
				}),
		}),

		...pender({
			type: GET_ENCARPAY_HYUNDAI_CREDIT_LIMIT,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const {
						data: { carMaximumLimit, interestRate, judgementStatus, hash, baseInterestRates },
					} = action.payload;

					draft.interestInfo = [
						{ financialCompany: 'HYUNDAICAPITAL', financialCompanyName: '현대캐피탈', interestRate },
						...(baseInterestRates ?? []),
					];
					draft.carMaximumLimit = carMaximumLimit;
					draft.judgementStatus = judgementStatus;
					draft.mainInterestRate = interestRate;
					draft.hash = hash;
				}),
		}),
		...pender({
			type: GET_ENCARPAY_BNK_CREDIT_LIMIT,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const {
						data: { carMaximumLimit, interestRate, judgementStatus, hash, baseInterestRates },
					} = action.payload;

					draft.interestInfo = [
						{ financialCompany: 'BNKCAPITAL', financialCompanyName: 'BNK캐피탈', interestRate },
						...(baseInterestRates ?? []),
					];
					draft.carMaximumLimit = carMaximumLimit;
					draft.judgementStatus = judgementStatus;
					draft.mainInterestRate = interestRate;
					draft.hash = hash;
				}),
		}),
		...pender({
			type: GET_ENCARPAY_JB_CREDIT_LIMIT,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const {
						data: { carMaximumLimit, interestRate, judgementStatus, hash },
					} = action.payload;

					draft.interestInfo = [
						{
							financialCompany: 'JBCAPITAL',
							financialCompanyName: 'JB우리캐피탈',
							interestRate,
						},
					];
					draft.carMaximumLimit = carMaximumLimit;
					draft.judgementStatus = judgementStatus;
					draft.mainInterestRate = interestRate;
					draft.hash = hash;
				}),
		}),
		...pender({
			type: POST_ENCARPAY_REAPPLY_CREDIT_LIMIT,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { data } = action.payload;
					const { financialCompany, id, judgement, interestRate } = data;
					const judgementKey = JUDGEMENT_KEY[financialCompany];

					draft.applicationId = id;
					draft.carMaximumLimit = data[judgementKey].carMaximumLimit;
					draft.judgementStatus = judgement.status;
					draft.applicationStorage = {
						...draft.applicationStorage,
						[data.id]: data,
					};

					draft.mainInterestRate = interestRate;
					draft.error = null;
				}),
			onError: onErrorReapply,
		}),
		...pender({
			type: POST_ENCARPAY_APPLICATIONS,
			onError,
		}),
		...pender({
			type: POST_ENCARPAY_CLIENTS,
			onError,
		}),
		...pender({
			type: POST_ENCARPAY_AGREEMENTS,
			onError,
		}),
		...pender({
			type: POST_ENCARPAY_HYUNDAI_CREDIT_LIMIT,
			onError,
		}),
	},
	initialState,
);
